import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { BookCheck, Plus, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";

const SPECIALTIES = ["ICU", "ER", "Med-Surg", "OR", "Cath Lab", "Respiratory Therapy", "Physical Therapy", "Radiology"];
const SKILL_SETS = {
  "ICU": ["Ventilator management", "Hemodynamic monitoring", "Central line care", "Titrating pressors", "ICP monitoring"],
  "ER": ["Triage", "IV starts (difficult)", "Trauma assessment", "Chest tube assist", "Code blue response"],
  "Med-Surg": ["IV medications", "Wound care", "Patient education", "Discharge planning", "Telemetry monitoring"],
  "OR": ["Circulating RN duties", "Surgical count", "Equipment sterilization", "Scrub tech support"],
  "Cath Lab": ["Hemodynamic monitoring", "Sheath removal", "IABP setup", "STEMI response"],
  "Respiratory Therapy": ["Ventilator management", "ABG analysis", "Intubation assist", "Nebulizer therapy"],
  "Physical Therapy": ["Gait training", "Therapeutic exercise", "Manual therapy", "Assessment"],
  "Radiology": ["CT positioning", "MRI safety", "Contrast administration", "Image reconstruction"],
};

export default function Skills() {
  const [skills, setSkills] = useState([]);
  const [spec, setSpec] = useState("ICU");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/skills/mine");
      setSkills(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addSkill = async (skill_name, rating, years) => {
    setLoading(true);
    try {
      await api.post("/skills", { specialty: spec, skill_name, self_rating: rating, years_experience: years });
      toast.success("Skill saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="skills-page">
      <div>
        <div className="overline text-trital-red">Verification</div>
        <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Clinical Skills Checklist</h1>
        <p className="text-slate-600 text-sm">Self-rate specialty-specific skills; recruiters & facilities verify.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {SPECIALTIES.map((s) => (
          <button key={s} onClick={() => setSpec(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${spec === s ? "bg-trital-navy text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid={`spec-${s}`}>{s}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {(SKILL_SETS[spec] || []).map((skillName) => {
          const existing = skills.find((sk) => sk.skill_name === skillName && sk.specialty === spec);
          return (
            <div key={skillName} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-slate-900">{skillName}</div>
                  <div className="mt-1 overline text-slate-500">{spec}</div>
                </div>
                {existing && (
                  <div className="flex items-center gap-1">
                    {existing.verified_by_recruiter && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold uppercase tracking-wider">Recruiter ✓</span>}
                    {existing.verified_by_facility && <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">Facility ✓</span>}
                  </div>
                )}
              </div>
              {existing ? (
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= existing.self_rating ? "fill-trital-red text-trital-red" : "text-slate-300"}`} />)}
                  </div>
                  <div className="text-slate-600">{existing.years_experience} yr{existing.years_experience === 1 ? "" : "s"}</div>
                </div>
              ) : (
                <SkillForm onSubmit={(r, y) => addSkill(skillName, r, y)} disabled={loading} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SkillForm({ onSubmit, disabled }) {
  const [rating, setRating] = useState(3);
  const [years, setYears] = useState(2);
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1" data-testid="skill-rating">
        {[1,2,3,4,5].map((i) => (
          <button type="button" key={i} onClick={() => setRating(i)} className="p-0.5">
            <Star className={`w-4 h-4 ${i <= rating ? "fill-trital-red text-trital-red" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <input type="number" value={years} min={0} max={50} onChange={(e) => setYears(Number(e.target.value))} className="w-20 px-2 py-1.5 rounded-lg border border-slate-300 text-sm" />
      <span className="text-xs text-slate-500">yrs</span>
      <button disabled={disabled} onClick={() => onSubmit(rating, years)} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-trital-navy text-white text-xs font-semibold btn-lift hover:bg-trital-navy-deep">
        <Plus className="w-3.5 h-3.5" /> Save
      </button>
    </div>
  );
}
